import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface DailyBonusPopupProps {
  isOpen: boolean;
  onClaim: () => void;
  streakDays: number;
  bonusPoints: number;
}

export const DailyBonusPopup = ({
  isOpen,
  onClaim,
  streakDays,
  bonusPoints,
}: DailyBonusPopupProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-8 top-[35%] -translate-y-1/2 z-50 mx-auto max-w-[200px]"
          >
            <div className="bg-gradient-to-br from-accent/20 via-card to-primary/10 rounded-xl p-4 text-center shadow-xl border border-accent/30">
              {/* Gift icon */}
              <motion.div
                initial={{ rotate: -10 }}
                animate={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mx-auto w-10 h-10 rounded-full bg-accent/30 flex items-center justify-center mb-2"
              >
                <Gift className="w-5 h-5 text-accent-foreground" />
              </motion.div>

              {/* Title */}
              <h2 className="text-sm font-bold text-foreground mb-1">
                Daily Bonus!
              </h2>

              {/* Streak display */}
              <div className="flex items-center justify-center gap-1 mb-2">
                <Flame className="w-3 h-3 text-warning" />
                <span className="text-xs font-semibold text-foreground">
                  Day {streakDays}
                </span>
              </div>

              {/* Bonus points */}
              <div className="bg-accent rounded-xl px-4 py-2 mb-3">
                <span className="text-2xl font-bold text-accent-foreground">
                  +{bonusPoints}
                </span>
                <p className="text-xs text-accent-foreground/80">
                  points
                </p>
              </div>

              {/* Claim button */}
              <Button 
                onClick={onClaim} 
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90" 
                size="sm"
              >
                Claim
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
